import { useMemo, useState } from 'react'
import { Box, DoorOpen, MapPin } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { FacilityStudio } from '@/components/equipment/facility/FacilityStudio'
import { cn } from '@/lib/utils'
import { usePermission } from '@/lib/permissions'
import { useFacilityLocations } from '@/hooks/useFacility'

/**
 * Where this unit stands, drawn (z8nrz7fw25): its building in miniature — every
 * floor, top storey first — with this unit's floor and room marked, and the
 * facility under it. The page below is the inside of the unit; this is the
 * outside, so together they are the whole address.
 */
export function UnitWhereabouts({ equipmentId, unitCode }: { equipmentId: string | null; unitCode: string }) {
  const { can } = usePermission()
  const locations = useFacilityLocations((can('equipment.read') || can('inventory.view')) && !!equipmentId)

  const [exploring, setExploring] = useState(false)
  const where = useMemo(() => {
    const tree = locations.data
    if (!tree || !equipmentId) return null
    for (const facility of tree.facilities) {
      for (const building of facility.buildings) {
        for (const floor of building.floors) {
          const room = floor.rooms.find((r) => r.units.some((u) => u.id === equipmentId))
          if (room) return { facility, building, floor, room }
        }
      }
    }
    const loose = tree.unmappedRooms.find((r) => r.units.some((u) => u.id === equipmentId))
    return loose ? { facility: null, building: null, floor: null, room: loose } : null
  }, [locations.data, equipmentId])

  if (!equipmentId || !locations.data) return null

  return (
    <Card>
      <CardContent className='space-y-2.5 p-3'>
        <div className='flex items-center justify-between gap-2 px-1'>
          <h2 className='flex items-center gap-1.5 text-xs font-semibold text-muted-foreground'>
            <MapPin className='h-3.5 w-3.5' /> Where {unitCode} is
          </h2>
          {where?.building && (
            <Button size='sm' variant='outline' className='h-7 gap-1 px-2 text-xs' onClick={() => setExploring(true)}>
              <Box className='h-3.5 w-3.5' /> View in 3D
            </Button>
          )}
        </div>
        {/* The whole floor in 3D, opened on this unit's room with the unit marked. */}
        <FacilityStudio open={exploring} onOpenChange={setExploring} focusUnitId={equipmentId} />

        {!where ? (
          <p className='rounded-lg border border-dashed px-3 py-4 text-center text-xs text-muted-foreground'>
            Not in a room yet. Set its room above.
          </p>
        ) : !where.building ? (
          <p className='flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm'>
            <DoorOpen className='h-4 w-4 text-primary' /> {where.room.label}
            <span className='text-xs text-muted-foreground'>· room not on a floor yet</span>
          </p>
        ) : (
          <figure aria-label={`${where.building.name}, ${where.floor?.name}, ${where.room.label}`}>
            <div className='flex items-center gap-1.5 rounded-t-lg border border-b-0 border-slate-300 bg-slate-100 px-2 py-1.5'>
              <span className='rounded bg-slate-800 px-1.5 text-[11px] font-bold text-white'>{where.building.code}</span>
              <figcaption className='truncate text-xs font-semibold text-slate-900'>{where.building.name}</figcaption>
            </div>
            <div className='overflow-hidden border border-slate-300'>
              {[...where.building.floors]
                .sort((a, b) => b.level - a.level)
                .map((f, i) => {
                  const here = f.id === where.floor?.id
                  return (
                    <div
                      key={f.id}
                      className={cn('grid grid-cols-[28px_minmax(0,1fr)]', i > 0 && 'border-t border-slate-200', here ? 'bg-primary/5' : 'bg-white')}
                    >
                      <span
                        className={cn(
                          'flex items-center justify-center border-r border-slate-200 text-xs font-semibold tabular-nums',
                          here ? 'bg-primary text-primary-foreground' : 'bg-slate-50 text-slate-500',
                        )}
                      >
                        {f.level}
                      </span>
                      <div className='min-w-0 px-2 py-1.5'>
                        <p className={cn('truncate text-[11px]', here ? 'font-semibold text-slate-900' : 'text-slate-500')}>{f.name}</p>
                        {here && (
                          <div className='mt-1 flex flex-wrap gap-1'>
                            {f.rooms.map((r) => (
                              <span
                                key={r.id}
                                className={cn(
                                  'rounded-md border px-1.5 py-0.5 text-[11px]',
                                  r.id === where.room.id
                                    ? 'border-primary bg-primary font-semibold text-primary-foreground'
                                    : 'border-slate-200 bg-white text-slate-500',
                                )}
                                aria-current={r.id === where.room.id ? 'location' : undefined}
                              >
                                {r.label}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
            </div>
            <div
              className='rounded-b-lg border-x border-b border-slate-300 px-2 py-1 text-[11px] text-slate-600'
              style={{ backgroundImage: 'repeating-linear-gradient(135deg, rgb(226 232 240) 0 5px, rgb(241 245 249) 5px 10px)' }}
            >
              <span className='rounded bg-white/90 px-1 font-medium'>
                {where.facility?.code} · {where.facility?.name}
              </span>
            </div>
            <p className='mt-2 text-xs text-slate-700'>
              {where.room.label}
              {where.room.name && <span className='text-muted-foreground'> · {where.room.name}</span>}
            </p>
          </figure>
        )}
      </CardContent>
    </Card>
  )
}
